import { FC, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useForm } from 'react-hook-form';
import axios from 'axios';

import { useLocalStorage } from '@/hooks/useLocalStorage';
import { API_BASE_URL } from '@/constants';
import { NbButton, NbCard, NbField, NbPageHeader } from '@/components/Neobrut';

interface FormData {
  email: string;
  password: string;
}

export const Login: FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setValue: setAccessToken } = useLocalStorage('access_token', undefined);
  const location = useLocation();

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setLoginError(null);
    try {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');

      const formattedData = { ...data, email: data.email.toLowerCase() };

      if (token && formattedData.email) {
        await axios.get(
          `${API_BASE_URL}/users/activate/?email=${formattedData.email}&token=${token}`,
        );
      }

      const response = await axios.post(`${API_BASE_URL}/token/`, formattedData);
      setAccessToken(response.data.access);
      setLoading(false);
      navigate('/');
    } catch (error) {
      console.error('Error logging in:', error);
      setLoading(false);
      setLoginError(
        'Inicio de sesión fallido. Por favor chequeá las credenciales e intentá de nuevo.',
      );
    }
  };

  return (
    <div className="mx-auto w-full max-w-md pb-10">
      <NbPageHeader
        slug="/ login"
        title={
          <>
            Entrar a<br />
            <span className="nb-highlight">tu cuenta.</span>
          </>
        }
        tone="plain"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="px-4 pt-4">
        <NbCard className="flex flex-col gap-4 p-4">
          <NbField
            id="email"
            label="Email"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email', { required: 'El campo email es requerido' })}
          />
          <NbField
            id="password"
            label="Contraseña"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password', { required: 'La contraseña es requerida' })}
          />

          {loginError && (
            <div
              className="text-[12px] font-semibold"
              style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
            >
              {loginError}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-left text-[12px] font-semibold underline decoration-2 underline-offset-2"
          >
            ¿Olvidaste tu contraseña?
          </button>

          <NbButton fill="ink" type="submit" className="w-full py-3" disabled={loading}>
            {loading ? 'Cargando…' : 'Iniciar sesión'}
          </NbButton>
        </NbCard>

        <div className="mt-4 text-center text-[13px] font-medium">
          ¿No tenés cuenta?{' '}
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="font-bold underline decoration-2 underline-offset-2"
          >
            Registrate
          </button>
        </div>
      </form>
    </div>
  );
};
